/**
 * Image Hotspots — Shop the Look
 * Custom element <hotspot-dot>
 */

if (!customElements.get('hotspot-dot')) {
  class HotspotDot extends HTMLElement {
    constructor() {
      super();
      this._isTooltipOpen = false;
      this._isModalOpen = false;
      this._onClick = this._onClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onModalClick = this._onModalClick.bind(this);
      this._onModalKeyDown = this._onModalKeyDown.bind(this);
    }

    connectedCallback() {
      this._tooltip = this.querySelector('.image-hotspots__tooltip');
      if (!this._tooltip) return;

      this._modal = this.querySelector('.image-hotspots__modal');
      this._modalOriginalParent = this._modal ? this._modal.parentNode : null;
      this._slider = this._modal ? this._modal.querySelector('.image-hotspots__panel-slider') : null;
      this._dialog = this._modal ? this._modal.querySelector('.image-hotspots__modal-dialog') : null;
      this._track = this._modal ? this._modal.querySelector('.image-hotspots__panel-track') : null;
      this._slides = this._track ? Array.from(this._track.querySelectorAll('.image-hotspots__panel-slide')) : [];
      this._currentSlide = 0;
      this._prevButton = this._modal ? this._modal.querySelector('.image-hotspots__panel-nav--prev') : null;
      this._nextButton = this._modal ? this._modal.querySelector('.image-hotspots__panel-nav--next') : null;
      this._currentCounter = this._modal ? this._modal.querySelector('.image-hotspots__panel-counter-current') : null;

      this._bindEvents();
      this._setInitialSlide();
      this._updateSliderUI();
    }

    disconnectedCallback() {
      this._closeAll();
      this._unbindEvents();
      document.removeEventListener('click', this._onDocumentClick);
    }

    _bindEvents() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeyDown);
      if (this._modal) {
        this._modal.addEventListener('click', this._onModalClick);
        this._modal.addEventListener('keydown', this._onModalKeyDown);
      }
    }

    _unbindEvents() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeyDown);
      if (this._modal) {
        this._modal.removeEventListener('click', this._onModalClick);
        this._modal.removeEventListener('keydown', this._onModalKeyDown);
      }
    }

    _onClick(e) {
      if (e.target.closest('.image-hotspots__tooltip-link')) return;

      if (e.target.closest('.image-hotspots__tooltip-trigger')) {
        e.preventDefault();
        e.stopPropagation();
        this._logEvent('tooltip_preview_click');
        this._openModal();
        return;
      }

      if (e.target.closest('.image-hotspots__tooltip')) return;

      e.preventDefault();
      this._toggle();
    }

    _onKeyDown(e) {
      if (e.key === 'Escape' && (this._isTooltipOpen || this._isModalOpen)) {
        e.preventDefault();
        this._closeAll();
        return;
      }

      if (e.target !== this) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._toggle();
        return;
      }

      if (!this._isModalOpen || this._slides.length <= 1) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._goToSlide(this._currentSlide + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._goToSlide(this._currentSlide - 1);
      }
    }

    _toggle() {
      if (this._isTooltipOpen) {
        this._closeTooltip();
      } else {
        this._openTooltip();
      }
    }

    _openTooltip() {
      var section = this.closest('[data-section-type="image-hotspots"]');
      if (section) {
        section.querySelectorAll('hotspot-dot').forEach(function(dot) {
          if (dot !== this) {
            dot._closeAll();
          }
        }.bind(this));
      }

      this._positionTooltip();
      this._isTooltipOpen = true;
      this.setAttribute('aria-expanded', 'true');
      this._tooltip.setAttribute('aria-hidden', 'false');
      document.addEventListener('click', this._onDocumentClick);
      this._logEvent('tooltip_open');
    }

    _closeTooltip() {
      this._isTooltipOpen = false;
      this.setAttribute('aria-expanded', 'false');
      this._tooltip.setAttribute('aria-hidden', 'true');
      this._tooltip.removeAttribute('data-position');
      this._tooltip.removeAttribute('data-align');

      if (!this._isModalOpen) {
        document.removeEventListener('click', this._onDocumentClick);
      }
      this._logEvent('tooltip_close');
    }

    _openModal() {
      if (!this._modal) {
        console.error('[image-hotspots] Modal element not found for hotspot');
        return;
      }
      this._closeTooltip();
      if (this._modal.parentNode !== document.body) {
        document.body.appendChild(this._modal);
      }
      this._setInitialSlide();
      this._updateSliderUI();
      this._isModalOpen = true;
      this._modal.setAttribute('aria-hidden', 'false');
      document.addEventListener('click', this._onDocumentClick);
      document.body.style.overflow = 'hidden';
      if (!this._dialog.getAttribute('tabindex')) {
        this._dialog.setAttribute('tabindex', '-1');
      }
      this._dialog.focus();
      this._logEvent('modal_open', { currentSlide: this._currentSlide + 1 });
    }

    _closeModal() {
      if (!this._modal) return;
      this._isModalOpen = false;
      this._modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this._modalOriginalParent && this._modal.parentNode !== this._modalOriginalParent) {
        this._modalOriginalParent.appendChild(this._modal);
      }

      if (!this._isTooltipOpen) {
        document.removeEventListener('click', this._onDocumentClick);
      }
      this._logEvent('modal_close');
    }

    _closeAll() {
      this._closeTooltip();
      this._closeModal();
    }

    _setInitialSlide() {
      if (!this._slides.length) return;
      var initialIndex = this._slider ? parseInt(this._slider.getAttribute('data-initial-slide') || '0', 10) : 0;
      this._currentSlide = Number.isNaN(initialIndex) ? 0 : initialIndex;
      this._currentSlide = Math.max(0, Math.min(this._slides.length - 1, this._currentSlide));
    }

    _goToSlide(index) {
      if (!this._slides.length) return;
      var safeIndex = Math.max(0, Math.min(this._slides.length - 1, index));
      if (safeIndex === this._currentSlide) {
        this._logEvent('modal_slide_edge', { slide: this._currentSlide + 1 });
        return;
      }
      var fromSlide = this._currentSlide + 1;
      this._currentSlide = safeIndex;
      this._updateSliderUI();
      this._logEvent('modal_slide_change', {
        from: fromSlide,
        to: this._currentSlide + 1
      });
    }

    _updateSliderUI() {
      if (!this._track || !this._slides.length) return;

      this._track.style.transform = 'translateX(-' + (this._currentSlide * 100) + '%)';

      if (this._currentCounter) {
        this._currentCounter.textContent = String(this._currentSlide + 1);
      }

      if (this._prevButton) {
        this._prevButton.disabled = this._currentSlide <= 0;
      }

      if (this._nextButton) {
        this._nextButton.disabled = this._currentSlide >= this._slides.length - 1;
      }
    }

    _onDocumentClick(e) {
      if (this._isModalOpen) {
        if (!this._dialog || !this._dialog.contains(e.target)) {
          this._logEvent('modal_overlay_click_close');
          this._closeModal();
        }
        return;
      }

      if (this._isTooltipOpen && !this.contains(e.target)) {
        this._closeTooltip();
      }
    }

    _onModalClick(e) {
      if (!this._isModalOpen) return;

      if (e.target.classList.contains('image-hotspots__modal')) {
        e.preventDefault();
        this._logEvent('modal_overlay_click_close');
        this._closeModal();
        return;
      }

      if (e.target.closest('.image-hotspots__panel-close')) {
        e.preventDefault();
        this._logEvent('modal_close_button_click');
        this._closeModal();
        return;
      }

      if (e.target.closest('.image-hotspots__panel-nav--prev')) {
        e.preventDefault();
        this._logEvent('modal_nav_prev_click', { currentSlide: this._currentSlide + 1 });
        this._goToSlide(this._currentSlide - 1);
        return;
      }

      if (e.target.closest('.image-hotspots__panel-nav--next')) {
        e.preventDefault();
        this._logEvent('modal_nav_next_click', { currentSlide: this._currentSlide + 1 });
        this._goToSlide(this._currentSlide + 1);
      }
    }

    _onModalKeyDown(e) {
      if (!this._isModalOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this._logEvent('modal_escape_close');
        this._closeModal();
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._logEvent('modal_nav_next_key', { currentSlide: this._currentSlide + 1 });
        this._goToSlide(this._currentSlide + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._logEvent('modal_nav_prev_key', { currentSlide: this._currentSlide + 1 });
        this._goToSlide(this._currentSlide - 1);
      }
    }

    _logEvent(eventName, data) {
      console.info('[image-hotspots][event]', eventName, data || {});
    }

    _positionTooltip() {
      var container = this.closest('.image-hotspots__image-container');
      if (!container) return;

      this._tooltip.removeAttribute('data-position');
      this._tooltip.removeAttribute('data-align');

      var containerRect = container.getBoundingClientRect();
      var dotRect = this.getBoundingClientRect();
      var tooltipWidth = window.matchMedia('(max-width: 768px)').matches ? 180 : 220;
      var tooltipEstimatedHeight = 280;
      var gap = 10;

      var spaceBelow = containerRect.bottom - dotRect.bottom;
      var spaceAbove = dotRect.top - containerRect.top;

      if (spaceBelow < tooltipEstimatedHeight + gap && spaceAbove > spaceBelow) {
        this._tooltip.setAttribute('data-position', 'above');
      }

      var dotCenterX = dotRect.left + dotRect.width / 2;
      var containerLeft = containerRect.left;
      var containerRight = containerRect.right;
      var halfTooltip = tooltipWidth / 2;

      if (dotCenterX - containerLeft < halfTooltip) {
        this._tooltip.setAttribute('data-align', 'left');
      } else if (containerRight - dotCenterX < halfTooltip) {
        this._tooltip.setAttribute('data-align', 'right');
      }
    }
  }

  customElements.define('hotspot-dot', HotspotDot);
}
